import React, { useState, useMemo } from "react";
import { JSONSchema6 } from 'json-schema';
import { ISubmitEvent, FormProps } from "@rjsf/core";
import { Button } from "semantic-ui-react";

import { useTranslation } from "react-i18next";
import { useApplicationConfiguration } from "../../configuration/ConfigurationContext";
import { IFormWithRules } from "../../../sg-schema/withRules";
import { EWidgetType } from "../../../sg-schema/utils/widgetRegistry";
import { JSONSchema, UiSchema } from "../../../sg-schema/RJSF";

import { useLoggedInUser } from "../../auth/hooks/useLoggedInUser";
import { EAuditStatus } from "../../interfaces/IAudit";
import { contextSchema, ISchemaContext } from "../../constants/schemaContext";
import { ObjectFieldTemplates } from "../form/jsonSchemaFields/write";
import { IFormContext } from "../../interfaces/IFormContext";
import { useAuthContext } from "../../auth/AuthenticationProvider";

interface ISchemaForm extends IFormWithRules {
    schema: JSONSchema;
    uiSchema: UiSchema;
    formData?: { [key: string]: any };
    rules?: { [key: string]: any }[];
    translations?: { [key: string]: any };

}

const SchemaForm: React.FC<ISchemaForm> = ({
    rules = [],
    schema,
    uiSchema,
    translations,
    formData = {},
    children,
    onSubmit,
    onError,
    onChange,
    liveValidate,
    ...otherProps
}) => {

    const config = useApplicationConfiguration();
    const Form = useMemo(() => config.jsonSchema.getForm(EWidgetType.Write), []); // eslint-disable-line
    const { t, i18n } = useTranslation();

    // If there's translations, add them to the namespace
    if (translations) {
        const keys = Object.keys(translations);
        keys.forEach((key) => {
            i18n.addResourceBundle(key, "schema", translations[key]);
        });
    }

    const [dirtyAfterPageLoad, setDirtyAfterPageLoad] = useState(false);
    const [hasErrors, setHasErrors] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    const handleError = (errors: any[]) => {
        setHasErrors(errors.length ? true : false)
        onError && onError(errors);
    }

    const handleSubmit = (form: ISubmitEvent<any>, nativeEvent: React.FormEvent<HTMLFormElement>) => {
        onSubmit && onSubmit(form.formData, nativeEvent);
        setSubmitted(true);
    };


    const handleChange: FormProps<any>["onChange"] = (form) => {
        onChange && onChange(form);
        setDirtyAfterPageLoad(true)
    };


    const user = useLoggedInUser();
    const { token } = useAuthContext();

    const roleList = user ? user.roles.map(({ reference }) => reference).filter((value) => value) : []

    const contextData: ISchemaContext = {
        roles: roleList,
        mode: "form",
        status: formData.status || formData.tila || EAuditStatus.Draft,
    };

    const formContext: IFormContext = {
        ...(otherProps.formContext || {}),
        objectFieldTemplates: ObjectFieldTemplates,
        apiToken: token,
        dirtyAfterPageLoad
    };


    return (
        <Form
            {...otherProps}
            className="ui form SchemaForm"
            schema={schema}
            uiSchema={uiSchema}
            formData={formData}
            onSubmit={handleSubmit}
            onError={handleError}
            onChange={handleChange}
            liveValidate={liveValidate || hasErrors || submitted}

            rules={rules}
            contextData={contextData}
            contextSchema={contextSchema}
            formContext={formContext}
            customFormats={{ "date": /.+/ }}
            children={children || <Button type="submit"> {t("Tallenna")} </Button>}
        />
    );
};

SchemaForm.defaultProps = {
    noHtml5Validate: true,
    liveValidate: false
}
export default SchemaForm;
